<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">塔林介绍</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ list.length }}</b> 个板块</span>
        <span class="divider">|</span>
        <span>显示中 <b>{{ statusCount(1) }}</b></span>
        <span class="divider">|</span>
        <span>已隐藏 <b>{{ statusCount(0) }}</b></span>
      </div>
    </div>

    <div class="tower-layout">
      <!-- 左：板块卡片流（按主页顺序） -->
      <el-card class="tower-card tower-list-card" shadow="never">
        <div slot="header" class="card-head">
          <span class="card-head-title">
            板块内容
            <span class="card-head-sub">上三块固定，下方内容块可调顺序</span>
          </span>
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增板块</el-button>
        </div>

        <div v-loading="loading" class="section-list">
          <div
            v-for="(row, index) in displayList"
            :key="row.id"
            class="section-item"
            :class="{ 'is-hidden': row.status !== 1 }"
          >
            <div class="item-order">{{ index + 1 }}</div>

            <div class="item-cover">
              <el-image v-if="row.cover" :src="row.cover" :preview-src-list="[row.cover]" fit="cover" style="width: 100%; height: 100%" />
              <span v-else class="cover-empty"><i class="el-icon-picture-outline" /> 未传封面</span>
            </div>

            <div class="item-main">
              <div class="item-head">
                <span class="item-title">{{ row.title || sectionLabel(row.key) }}</span>
                <el-tag v-if="isFixed(row.key)" size="mini" type="warning" effect="plain">固定位置</el-tag>
                <el-tag v-if="row.link_url" size="mini" type="success" effect="plain">外链</el-tag>
                <el-tag v-if="row.status !== 1" size="mini" type="info" effect="plain">已隐藏</el-tag>
              </div>
              <div class="item-sub">{{ row.subtitle || '暂无卡片小字' }}</div>
              <div class="item-content">{{ row.link_url || stripHtml(row.content) || '暂无图文详情' }}</div>
              <div class="item-time">更新于 {{ row.updated_at }}</div>
            </div>

            <div class="item-ops">
              <div class="switch-wrap">
                <el-switch :value="row.status === 1" @change="val => toggleShow(row, val)" />
                <span class="switch-label">{{ row.status === 1 ? '显示' : '隐藏' }}</span>
              </div>
              <div v-if="!isFixed(row.key)" class="move-group">
                <el-tooltip content="上移" placement="top">
                  <el-button size="mini" icon="el-icon-top" :disabled="movableIndexOf(row) <= 0" @click="handleMove(row, -1)" />
                </el-tooltip>
                <el-tooltip content="下移" placement="top">
                  <el-button
                    size="mini" icon="el-icon-bottom"
                    :disabled="movableIndexOf(row) >= movableList.length - 1"
                    @click="handleMove(row, 1)"
                  />
                </el-tooltip>
              </div>
              <div v-else class="move-fixed">位置固定</div>
              <div class="op-links">
                <el-button type="text" size="mini" @click="handleEdit(row)">编辑</el-button>
                <el-button
                  v-if="!isFixed(row.key)"
                  type="text" size="mini" class="text-danger-btn"
                  @click="handleDelete(row)"
                >删除</el-button>
              </div>
            </div>
          </div>

          <div v-if="!list.length && !loading" class="list-empty">暂无板块，点右上角「新增板块」创建</div>
        </div>
      </el-card>

      <!-- 右：小程序端效果 -->
      <el-card class="tower-card tower-preview-card" shadow="never">
        <div slot="header" class="card-head">
          <span class="card-head-title">
            小程序端效果
            <span class="card-head-sub">随左侧内容实时变化</span>
          </span>
        </div>

        <div class="phone">
          <div class="phone-nav">圣山塔林</div>
          <div class="phone-scroll">
            <!-- 顶部大图 -->
            <div class="phone-hero">
              <el-image v-if="hero && hero.cover" :src="hero.cover" :preview-src-list="[hero.cover]" fit="cover" style="width: 100%; height: 100%" />
              <div v-else class="phone-hero-empty">
                <span class="hero-glyph">塔</span>
                <span>{{ hero ? '主页大图未上传' : '未设置主页大图板块' }}</span>
              </div>
            </div>

            <!-- 按设计稿分形态：深底文字板块 / 米白双列小卡 / 白底缩略图卡 -->
            <template v-for="block in previewBlocks">
              <!-- 文字板块（如：塔林介绍） -->
              <div v-if="block.type === 'text'" :key="block.key" class="phone-plain">
                <div class="phone-plain-title">{{ block.s.title || sectionLabel(block.s.key) }}</div>
                <p class="phone-plain-text">{{ stripHtml(block.s.content) }}</p>
              </div>

              <!-- 双列小卡（如：塔林文化 / 服务介绍） -->
              <div v-else-if="block.type === 'grid'" :key="block.key" class="phone-grid">
                <div v-for="(s, gi) in block.items" :key="s.id" class="phone-cell">
                  <div class="phone-cell-title">{{ s.title || sectionLabel(s.key) }}</div>
                  <div class="phone-cell-sub">{{ s.subtitle || '—' }}</div>
                  <span class="phone-cell-tag">{{ gi === 0 ? '点击了解' : '查看介绍' }}</span>
                </div>
              </div>

              <!-- 下方内容块：一行一个（图左文右） -->
              <div v-else :key="block.key" class="phone-wide">
                <div class="phone-wide-thumb">
                  <el-image v-if="block.s.cover" :src="block.s.cover" :preview-src-list="[block.s.cover]" fit="cover" style="width: 100%; height: 100%" />
                  <span v-else class="thumb-glyph">塔</span>
                </div>
                <div class="phone-wide-main">
                  <div class="phone-wide-head">
                    <span class="phone-wide-title">{{ block.s.title || sectionLabel(block.s.key) }}</span>
                    <span class="phone-arrow phone-arrow--ghost"><i class="el-icon-arrow-right" /></span>
                  </div>
                  <p class="phone-wide-desc">{{ stripHtml(block.s.content) || block.s.subtitle || '' }}</p>
                </div>
              </div>
            </template>
          </div>
          <div class="phone-cta">前往预约</div>
        </div>
      </el-card>
    </div>

    <!-- 编辑/新增弹框 -->
    <el-dialog
      :title="isEdit ? `编辑「${sectionLabel(form.key)}」板块` : '新增板块'"
      :visible.sync="formVisible"
      width="780px"
      top="4vh"
      class="tower-form-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="100px" label-position="top">
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="所属板块" prop="key">
              <el-select v-model="form.key" placeholder="请选择" style="width: 100%">
                <el-option v-for="d in sections" :key="d.value" :label="d.label" :value="d.value" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="标题" prop="title">
              <el-input v-model="form.title" placeholder="如：塔林文化；顶部大图板块可留空" maxlength="64" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="卡片小字" prop="subtitle">
          <el-input v-model="form.subtitle" placeholder="如：千年传承 历史积淀（空格分隔的词会在主页上分行显示）" maxlength="64" />
        </el-form-item>
        <el-form-item label="封面图" prop="cover">
          <image-uploader v-model="form.cover" />
          <span class="form-tip">卡片上的缩略图</span>
        </el-form-item>
        <el-form-item label="详情方式">
          <el-radio-group v-model="detailType">
            <el-radio label="content">图文详情</el-radio>
            <el-radio label="link">外链</el-radio>
          </el-radio-group>
          <span class="form-tip">外链=点板块卡片后跳转外部链接，不再展示图文页</span>
        </el-form-item>
        <el-form-item v-if="detailType === 'content'" label="图文详情" prop="content">
          <wechat-editor v-model="form.content" />
          <span class="form-tip">点板块卡片上的箭头后展开的图文页</span>
        </el-form-item>
        <el-form-item v-else label="外链地址" prop="link_url">
          <el-input v-model="form.link_url" placeholder="https://…" maxlength="500" />
          <span class="form-tip">需以 http(s):// 开头</span>
        </el-form-item>
        <el-form-item label="是否显示" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">显示</el-radio>
            <el-radio :label="0">隐藏</el-radio>
          </el-radio-group>
          <span class="form-tip">隐藏后塔林主页不再展示该板块</span>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="formVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">保 存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getTowerList, createTower, updateTower, deleteTower, setTowerStatus, moveTower
} from '@/api/tower'
import dictOptions from '@/mixins/dict-options'
import ImageUploader from '@/components/ImageUploader.vue'
import WechatEditor from '@/components/WechatEditor.vue'

// 固定板块：顶部大图 + 塔林介绍 / 塔林文化 / 服务介绍，位置不可调
const FIXED_ORDER = ['banner', 'intro', 'culture', 'service']

export default {
  name: 'TowerSectionManage',
  components: { ImageUploader, WechatEditor },
  mixins: [dictOptions],
  data() {
    return {
      loading: false,
      list: [],
      sections: [],
      formVisible: false,
      currentRow: null,
      submitting: false,
      form: this.emptyForm(),
      // 详情方式（仅表单内的展示开关，不提交）：link_url 非空即外链，否则图文详情
      detailType: 'content',
      rules: {
        key: [{ required: true, message: '请选择所属板块', trigger: 'change' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      }
    }
  },
  computed: {
    isEdit() { return !!(this.currentRow && this.currentRow.id) },
    // 展示顺序：顶部大图 + 固定三块（塔林介绍/塔林文化/服务介绍）在前，其余内容块按排序排在其后
    displayList() {
      const fixed = []
      FIXED_ORDER.forEach(key => {
        const hit = this.list.find(i => i.key === key)
        if (hit) fixed.push(hit)
      })
      const rest = this.list.filter(i => !this.isFixed(i.key)).sort((a, b) => a.sort - b.sort)
      return fixed.concat(rest)
    },
    // 可调序部分（只有下面的内容块能调）
    movableList() {
      return this.list.filter(i => !this.isFixed(i.key)).sort((a, b) => a.sort - b.sort)
    },
    hero() {
      return this.list.find(i => i.key === 'banner') || null
    },
    // 预览：固定三块 + 下方一行一个内容块
    previewBlocks() {
      const visible = key => {
        const hit = this.list.find(i => i.key === key)
        return hit && hit.status === 1 ? hit : null
      }
      const blocks = []
      const intro = visible('intro')
      if (intro) blocks.push({ type: 'text', key: 'text-intro', s: intro })
      const grid = ['culture', 'service'].map(visible).filter(Boolean)
      if (grid.length) blocks.push({ type: 'grid', key: 'grid-fixed', items: grid })
      this.movableList.filter(i => i.status === 1).forEach(s => {
        blocks.push({ type: 'row', key: 'row' + s.id, s })
      })
      return blocks
    }
  },
  created() {
    this.loadData()
    this.loadDictOptions({ tower_section: 'sections' })
  },
  methods: {
    loadData() {
      this.loading = true
      getTowerList().then(res => {
        if (res.code === 200) {
          // 后端 sort 升序已保证；固定块展示顺序由 displayList 按 key 重排
          this.list = res.data.list || []
        }
      }).finally(() => {
        this.loading = false
      })
    },
    statusCount(val) { return this.list.filter(i => i.status === val).length },
    isFixed(key) { return FIXED_ORDER.indexOf(key) > -1 },
    movableIndexOf(row) { return this.movableList.findIndex(i => i.id === row.id) },
    sectionLabel(value) {
      const item = this.sections.find(s => String(s.value) === String(value))
      return item ? item.label : (value || '—')
    },
    stripHtml(html) {
      if (!html) return ''
      const text = String(html)
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim()
      return text.length > 66 ? text.slice(0, 66) + '…' : text
    },
    // 显示/隐藏：开关直接生效
    toggleShow(row, val) {
      const next = val ? 1 : 0
      if (row.status === next) return
      setTowerStatus(row.id, { status: next }).then(res => {
        if (res.code !== 200) return
        this.$message.success(next === 1 ? '已显示，主页恢复展示' : '已隐藏，主页不再展示')
        this.loadData()
      })
    },
    // 上移 / 下移：只在下方内容块之间调序（固定块后端拒绝，前端按钮也已禁用）
    handleMove(row, dir) {
      moveTower(row.id, { direction: dir === -1 ? 'up' : 'down' }).then(res => {
        if (res.code !== 200) return
        this.loadData()
      })
    },
    handleAdd() {
      this.currentRow = null
      this.form = this.emptyForm()
      this.detailType = 'content'
      this.formVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleEdit(row) {
      this.currentRow = Object.assign({}, row)
      this.form = Object.assign(this.emptyForm(), row)
      this.detailType = row.link_url ? 'link' : 'content'
      this.formVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleDelete(row) {
      this.$confirm(`确认删除「${this.sectionLabel(row.key)}」板块？删除后小程序端不再展示该板块。`, '删除确认', { type: 'warning' })
        .then(() => {
          deleteTower(row.id).then(res => {
            if (res.code !== 200) return
            this.$message.success('已删除')
            this.loadData()
          })
        }).catch(() => {})
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const payload = Object.assign({}, this.form)
        // 两种详情方式互斥：外链时清掉富文本，图文时清掉外链（link_url 非空即外链，不另设类型字段）
        if (this.detailType === 'link') {
          payload.content = ''
        } else {
          payload.link_url = ''
        }
        // sort 无输入项：编辑原样回传（不丢库里已有值），新增不传由后端排在最后
        if (!payload.id) delete payload.sort
        this.submitting = true
        const req = payload.id ? updateTower(payload.id, payload) : createTower(payload)
        req.then(res => {
          if (res.code !== 200) return
          this.$message.success(payload.id ? '已保存' : '已新增，排在最后，可用箭头调整顺序')
          this.formVisible = false
          this.loadData()
        }).finally(() => {
          this.submitting = false
        })
      })
    },
  emptyForm() {
    return {
      id: 0,
      key: '',
      title: '',
      subtitle: '',
      cover: '',
      content: '',
      link_url: '',
      sort: 0,
      status: 1
    }
  }
  }
}
</script>

<style scoped>
/* ===== 左右分栏骨架 ===== */
.tower-layout {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 16px;
}

.tower-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.tower-list-card {
  flex: 1 1 auto;
  min-width: 0;
}

.tower-preview-card {
  flex: 0 0 420px;
}

.tower-card >>> .el-card__header {
  flex-shrink: 0;
  padding: 10px 16px;
  background: #fafafa;
}

.tower-card >>> .el-card__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 12px 16px;
}

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.card-head-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.card-head-sub {
  font-size: 12px;
  font-weight: 400;
  color: #909399;
}

/* ===== 左：板块卡片流 ===== */
.section-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 4px;
}

.section-item {
  display: flex;
  gap: 12px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px;
  transition: box-shadow 0.2s, opacity 0.2s;
}

.section-item:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.section-item.is-hidden {
  opacity: 0.55;
  background: #fafafa;
}

.item-order {
  flex: 0 0 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--primary);
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.is-hidden .item-order {
  background: #c0c4cc;
}

.item-cover {
  flex: 0 0 120px;
  height: 84px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f6f8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.cover-empty {
  font-size: 12px;
  color: #c0c4cc;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  width: 100%;
  height: 100%;
  justify-content: center;
  box-sizing: border-box;
  background: #fafafa;
}

.item-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.item-head {
  display: flex;
  align-items: center;
  gap: 6px;
}

.item-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.item-sub {
  font-size: 12px;
  color: #909399;
  margin-top: 3px;
}

.item-content {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  margin-top: 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-time {
  font-size: 12px;
  color: #c0c4cc;
  margin-top: auto;
  padding-top: 6px;
}

.item-ops {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
}

.switch-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.switch-label {
  font-size: 12px;
  color: #909399;
}

.move-group {
  display: flex;
  gap: 4px;
}

.move-group .el-button {
  padding: 5px 8px;
  margin: 0;
}

.move-fixed {
  font-size: 12px;
  color: #c0c4cc;
}

.op-links {
  display: flex;
}

.op-links .el-button + .el-button {
  margin-left: 10px;
}

.list-empty {
  padding: 60px 0;
  text-align: center;
  color: #c0c4cc;
  font-size: 13px;
}

/* ===== 右：小程序端预览（对齐塔林主页深色设计稿） ===== */
.phone {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  background: #191410;
  border: 1px solid #2c241d;
}

.phone-nav {
  flex: 0 0 auto;
  padding: 12px;
  text-align: center;
  color: #f5f0e6;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 4px;
  font-family: 'STKaiti', 'KaiTi', serif;
}

.phone-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 12px 12px;
}

.phone-hero {
  height: 150px;
  border-radius: 8px;
  overflow: hidden;
  background: linear-gradient(160deg, #3a2f26, #241c15);
  margin-bottom: 12px;
}

.phone-hero img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.phone-hero-empty {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #8a7a64;
  font-size: 12px;
}

.hero-glyph {
  font-size: 34px;
  color: #b39b6d;
  font-family: 'STKaiti', 'KaiTi', serif;
  line-height: 1;
}

/* 深底文字板块（塔林介绍） */
.phone-plain {
  padding: 2px 4px;
  margin-bottom: 14px;
}

.phone-plain-title {
  color: #f2ead9;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 6px;
}

.phone-plain-text {
  margin: 0;
  font-size: 11px;
  color: #a89a86;
  line-height: 1.8;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 米白双列小卡 */
.phone-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}

.phone-cell {
  background: #f2e8da;
  border-radius: 8px;
  padding: 12px 10px 10px;
  min-height: 86px;
}

.phone-cell-title {
  color: #4a3526;
  font-size: 13px;
  font-weight: 700;
}

.phone-cell-sub {
  margin-top: 4px;
  font-size: 10px;
  color: #97887a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.phone-cell-tag {
  display: inline-block;
  margin-top: 10px;
  padding: 3px 10px;
  border-radius: 999px;
  background: #f6e0da;
  color: var(--primary);
  font-size: 10px;
  line-height: 1;
}

/* 白底缩略图卡（图左文右） */
.phone-wide {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #f7f4ef;
  border-radius: 8px;
  padding: 12px 12px 12px 10px;
  margin-bottom: 10px;
}

.phone-wide-thumb {
  flex: 0 0 72px;
  height: 72px;
  border-radius: 6px;
  overflow: hidden;
  background: #e5dccb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.phone-wide-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.phone-wide-main {
  flex: 1;
  min-width: 0;
}

.phone-wide-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.phone-wide-title {
  color: #3f2f22;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1px;
}

.phone-wide-desc {
  margin: 6px 0 0;
  font-size: 11px;
  color: #8d7f70;
  line-height: 1.7;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.thumb-glyph {
  color: #a08a68;
  font-size: 20px;
  font-family: 'STKaiti', 'KaiTi', serif;
}

/* 红圈箭头（白卡右上角，描边款） */
.phone-arrow {
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
}

.phone-arrow--ghost {
  border: 1px solid var(--primary);
  color: var(--primary);
  background: transparent;
}

.phone-cta {
  flex: 0 0 auto;
  margin: 0 12px 12px;
  padding: 11px 0;
  text-align: center;
  background: var(--primary);
  color: #fff;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 2px;
}

/* ===== 弹框 ===== */
.form-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.tower-form-dialog >>> .el-dialog__body {
  padding: 14px 30px 4px;
}

.tower-form-dialog >>> .wechat-editor {
  min-height: 320px;
}
</style>
